/**
 * @file DefineImportTypesTab.js
 * @description Manage the list of import types for a custom import template.
 */

import { useState } from 'react';
import PropTypes from 'prop-types';
import ImportTypeDropdown from '../shared/asyncDropdowns/ImportTypeDropdown';
import style from './ImportTemplates.module.scss';
import { getText } from '../../i18n';

const createBlankTemplateType = (templateId = 0, processingOrder = 1) => ({
    id: 0,
    importTemplateId: templateId,
    importTypeId: null,
    importTypeName: '',
    name: '',
    processingOrder,
    templateFields: [],
    importMapGroups: [],
});

const isTemplateTypeBlank = templateType => {
    const hasImportType = !!templateType?.importTypeId;
    const hasName = !!(templateType?.name || '').trim();
    const hasFields = (templateType?.templateFields || []).length > 0;
    const hasMappings = (templateType?.importMapGroups || []).length > 0;

    return !hasImportType && !hasName && !hasFields && !hasMappings;
};

const normalizeTemplateTypesForUi = (templateTypes = [], templateId = 0) => {
    const seededTemplateTypes =
        templateTypes.length > 0
            ? [...templateTypes]
            : [createBlankTemplateType(templateId, 1)];

    let blankRowSeen = false;

    const collapsedTemplateTypes = seededTemplateTypes.filter(templateType => {
        if (!isTemplateTypeBlank(templateType)) {
            return true;
        }

        if (blankRowSeen) {
            return false;
        }

        blankRowSeen = true;
        return true;
    });

    return collapsedTemplateTypes.map((templateType, index) => ({
        ...createBlankTemplateType(templateId, index + 1),
        ...templateType,
        importTemplateId: templateType?.importTemplateId ?? templateId,
        processingOrder: index + 1,
        templateFields: templateType?.templateFields || [],
        importMapGroups: templateType?.importMapGroups || [],
    }));
};

const DefineImportTypesTab = ({
    templateData,
    setTemplateData,
    onEditFields,
    onEditMapping,
    isSubmitting = false,
}) => {
    const templateId = templateData?.id || 0;
    const templateTypes = normalizeTemplateTypesForUi(
        templateData?.templateTypes || [],
        templateId,
    );
    const hasConfiguredImportTypes = templateTypes.some(
        templateType => !isTemplateTypeBlank(templateType),
    );

    const addButtonLabel = getText('msg_4cb1a237d812');
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    const updateTemplateTypes = updatedTemplateTypes => {
        const normalizedTemplateTypes = normalizeTemplateTypesForUi(
            updatedTemplateTypes,
            templateId,
        );

        setTemplateData(prevData => ({
            ...prevData,
            templateTypes: normalizedTemplateTypes,
        }));
    };

    const resetDragState = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleAddImportType = () => {
        if (isSubmitting) {
            return;
        }

        const hasBlankRow = templateTypes.some(isTemplateTypeBlank);

        if (hasBlankRow) {
            return;
        }

        updateTemplateTypes([
            ...templateTypes,
            createBlankTemplateType(templateId, templateTypes.length + 1),
        ]);
    };

    const handleDeleteImportType = index => {
        if (isSubmitting) {
            return;
        }

        if (wouldCreateSingleTypeImportTypeMismatch(index)) {
            return;
        }

        const updatedTemplateTypes = [...templateTypes];
        updatedTemplateTypes.splice(index, 1);
        updateTemplateTypes(updatedTemplateTypes);
    };

    const handleTypeChange = (selectedType, index) => {
        if (isSubmitting) {
            return;
        }

        const updatedTemplateTypes = [...templateTypes];
        updatedTemplateTypes[index] = {
            ...updatedTemplateTypes[index],
            importTypeId: selectedType?.id || null,
            importTypeName: selectedType?.name || '',
        };

        updateTemplateTypes(updatedTemplateTypes);
    };

    const handleNameChange = (event, index) => {
        if (isSubmitting) {
            return;
        }

        const { value } = event.target;
        const updatedTemplateTypes = [...templateTypes];
        updatedTemplateTypes[index] = {
            ...updatedTemplateTypes[index],
            name: value,
        };

        updateTemplateTypes(updatedTemplateTypes);
    };

    const handleDragStart = (event, index) => {
        if (isSubmitting) {
            event.preventDefault();
            return;
        }

        setDraggedIndex(index);
        setDragOverIndex(index);
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(index));
    };

    const handleDragEnter = index => {
        if (draggedIndex === null || draggedIndex === index) {
            return;
        }

        setDragOverIndex(index);
    };

    const handleDragOver = (event, index) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';

        if (draggedIndex === null || draggedIndex === index) {
            return;
        }

        if (dragOverIndex !== index) {
            setDragOverIndex(index);
        }
    };

    const handleDrop = (event, targetIndex) => {
        event.preventDefault();

        if (
            draggedIndex === null ||
            targetIndex === null ||
            draggedIndex === targetIndex
        ) {
            resetDragState();
            return;
        }

        const updatedTemplateTypes = [...templateTypes];
        const [movedTemplateType] = updatedTemplateTypes.splice(
            draggedIndex,
            1,
        );

        // Dropping onto a row inserts:
        // - below that row when dragging downward
        // - above that row when dragging upward
        updatedTemplateTypes.splice(targetIndex, 0, movedTemplateType);

        updateTemplateTypes(updatedTemplateTypes);
        resetDragState();
    };

    const hasDefinedFields = templateType =>
        (templateType?.templateFields || []).some(field => field.importFieldId);

    const canEditFields = templateType => !!templateType?.importTypeId;

    const canEditMapping = templateType =>
        !!templateType?.importTypeId && hasDefinedFields(templateType);

    const wouldCreateSingleTypeImportTypeMismatch = index => {
        if (!(templateId > 0) || !templateData?.importTypeId) {
            return false;
        }

        const remainingConfiguredTemplateTypes = templateTypes
            .filter((_, templateTypeIndex) => templateTypeIndex !== index)
            .filter(templateType => !isTemplateTypeBlank(templateType));

        if (remainingConfiguredTemplateTypes.length !== 1) {
            return false;
        }

        return (
            remainingConfiguredTemplateTypes[0].importTypeId !==
            templateData.importTypeId
        );
    };

    const getMappingButtonTitle = templateType => {
        if (!templateType?.importTypeId) {
            return getText('msg_056a545aac76');
        }

        if (!hasDefinedFields(templateType)) {
            return getText('msg_903d5df3957e');
        }

        return getText('msg_569b12ab04ec');
    };

    const getImportTypeRowLabel = index =>
        getText('msg_68acae463f96', { index: index + 1 });

    const getRowStyle = index => {
        const isDragging = draggedIndex === index;
        const isDropTarget =
            dragOverIndex === index &&
            draggedIndex !== null &&
            draggedIndex !== index;

        return {
            border: isDropTarget
                ? '2px dashed #0d6efd'
                : '2px dashed transparent',
            backgroundColor: isDropTarget ? '#f4f8ff' : 'transparent',
            opacity: isDragging ? 0.55 : 1,
            borderRadius: '8px',
            paddingTop: '8px',
            paddingBottom: '8px',
            transition:
                'background-color 120ms ease, border-color 120ms ease, opacity 120ms ease',
        };
    };

    return (
        <div className={`${style.defineFieldsContainer} mt-5`}>
            <div className="col-12 col-xs-12 col-md-10 col-lg-10 m-auto">
                <div
                    className={`${style.importTypeIntro} mb-4 small text-muted`}
                >
                    {getText('msg_bc90abcc0a66')}
                </div>

                <div
                    className={`${style.importTypeHeader} d-none d-lg-grid mb-3`}
                >
                    <div />
                    <div className={style.importTypeHeaderLabel}>
                        {getText('msg_78cfb1f529c1')}
                    </div>
                    <div className={style.importTypeHeaderLabel}>
                        {getText('msg_09a3c8dbb880')}
                    </div>
                    <div />
                </div>

                {templateTypes.map((templateType, index) => (
                    <div
                        key={`${templateType.id}-${index}`}
                        className={`${style.importTypeRow} mb-3`}
                        style={getRowStyle(index)}
                        onDragEnter={() => handleDragEnter(index)}
                        onDragOver={event => handleDragOver(event, index)}
                        onDrop={event => handleDrop(event, index)}
                    >
                        <div className={style.importTypeMainRow}>
                            <div className={style.dragCell}>
                                <button
                                    type="button"
                                    draggable={!isSubmitting}
                                    onDragStart={event =>
                                        handleDragStart(event, index)
                                    }
                                    onDragEnd={resetDragState}
                                    className={style.dragHandle}
                                    title={getText('msg_e7541faf0a7e')}
                                    aria-label={`${getText('msg_e7541faf0a7e')} ${getImportTypeRowLabel(index)}`}
                                    disabled={isSubmitting}
                                >
                                    <i className="bi bi-grip-vertical" />
                                </button>
                            </div>

                            <div className={style.fieldCell}>
                                <label className="form-label d-lg-none mb-1">
                                    {getText('msg_78cfb1f529c1')}
                                </label>
                                <ImportTypeDropdown
                                    selectedType={
                                        templateType.importTypeId
                                            ? {
                                                  id: templateType.importTypeId,
                                                  name: templateType.importTypeName,
                                              }
                                            : null
                                    }
                                    inlineLabel={false}
                                    onTypeChange={selectedType =>
                                        handleTypeChange(selectedType, index)
                                    }
                                    required
                                    disabled={
                                        isSubmitting ||
                                        (templateId > 0 && templateType.id > 0)
                                    }
                                />
                            </div>

                            <div className={style.fieldCell}>
                                <label className="form-label d-lg-none mb-1">
                                    {getText('msg_09a3c8dbb880')}
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={templateType.name || ''}
                                    onChange={event =>
                                        handleNameChange(event, index)
                                    }
                                    placeholder={getText('msg_6edbbc06cfbf')}
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div className={style.actionsCell}>
                                <button
                                    type="button"
                                    className={`${style.iconActionTile} ${style.iconActionDanger}`}
                                    onClick={() =>
                                        handleDeleteImportType(index)
                                    }
                                    title={getText('msg_f6fdbe48dc54')}
                                    aria-label={`${getText('msg_f6fdbe48dc54')} ${getImportTypeRowLabel(index)}`}
                                    disabled={
                                        isSubmitting ||
                                        wouldCreateSingleTypeImportTypeMismatch(
                                            index,
                                        )
                                    }
                                >
                                    <i className="bi bi-trash3" />
                                </button>

                                <button
                                    type="button"
                                    className={`${style.iconActionTile} ${style.iconActionPrimary}`}
                                    onClick={() => onEditFields(index)}
                                    title={
                                        canEditFields(templateType)
                                            ? getText('msg_b4f2c16f90df')
                                            : getText('msg_056a545aac76')
                                    }
                                    aria-label={
                                        canEditFields(templateType)
                                            ? `${getText('msg_b4f2c16f90df')} ${getImportTypeRowLabel(index)}`
                                            : getText('msg_056a545aac76')
                                    }
                                    disabled={
                                        isSubmitting ||
                                        !canEditFields(templateType)
                                    }
                                >
                                    <svg
                                        width="22"
                                        height="22"
                                        viewBox="0 0 18 9"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M1.6875 5.625C1.87382 5.625 2.02466 5.77599 2.02478 5.96228V6.97522H3.03772C3.22401 6.97534 3.375 7.12618 3.375 7.3125C3.375 7.49882 3.22401 7.64966 3.03772 7.64978H2.02478V8.66272C2.02466 8.84902 1.87382 9 1.6875 9C1.50118 9 1.35034 8.84902 1.35022 8.66272V7.64978H0.33728C0.150985 7.64966 0 7.49882 0 7.3125C3.62254e-08 7.12618 0.150985 6.97534 0.33728 6.97522H1.35022V5.96228C1.35034 5.77599 1.50118 5.625 1.6875 5.625Z"
                                            fill="#1366FF"
                                        />
                                        <path
                                            fill-rule="evenodd"
                                            clip-rule="evenodd"
                                            d="M16.3125 5.625C17.2445 5.625 18 6.38052 18 7.3125C18 8.24448 17.2445 9 16.3125 9H6.1875C5.25552 9 4.5 8.24448 4.5 7.3125C4.5 6.38052 5.25552 5.625 6.1875 5.625H16.3125ZM6.1875 6.46875C5.72151 6.46875 5.34375 6.84651 5.34375 7.3125C5.34375 7.77849 5.72151 8.15625 6.1875 8.15625H16.3125C16.7785 8.15625 17.1562 7.77849 17.1562 7.3125C17.1562 6.84651 16.7785 6.46875 16.3125 6.46875H6.1875Z"
                                            fill="#1366FF"
                                        />
                                        <path
                                            d="M1.6875 0C1.87382 0 2.02466 0.150985 2.02478 0.33728V1.35022H3.03772C3.22401 1.35034 3.375 1.50118 3.375 1.6875C3.375 1.87382 3.22401 2.02466 3.03772 2.02478H2.02478V3.03772C2.02466 3.22401 1.87382 3.375 1.6875 3.375C1.50118 3.375 1.35034 3.22401 1.35022 3.03772V2.02478H0.33728C0.150985 2.02466 0 1.87382 0 1.6875C3.62254e-08 1.50118 0.150985 1.35034 0.33728 1.35022H1.35022V0.33728C1.35034 0.150985 1.50118 3.62251e-08 1.6875 0Z"
                                            fill="#1366FF"
                                        />
                                        <path
                                            fill-rule="evenodd"
                                            clip-rule="evenodd"
                                            d="M16.3125 0C17.2445 0 18 0.75552 18 1.6875C18 2.61948 17.2445 3.375 16.3125 3.375H6.1875C5.25552 3.375 4.5 2.61948 4.5 1.6875C4.5 0.75552 5.25552 0 6.1875 0H16.3125ZM6.1875 0.84375C5.72151 0.84375 5.34375 1.22151 5.34375 1.6875C5.34375 2.15349 5.72151 2.53125 6.1875 2.53125H16.3125C16.7785 2.53125 17.1562 2.15349 17.1562 1.6875C17.1562 1.22151 16.7785 0.84375 16.3125 0.84375H6.1875Z"
                                            fill="#1366FF"
                                        />
                                    </svg>
                                </button>

                                <button
                                    type="button"
                                    className={`${style.iconActionTile} ${style.iconActionSuccess}`}
                                    onClick={() => onEditMapping(index)}
                                    title={getMappingButtonTitle(templateType)}
                                    aria-label={
                                        canEditMapping(templateType)
                                            ? `${getText('msg_569b12ab04ec')} ${getImportTypeRowLabel(index)}`
                                            : getMappingButtonTitle(
                                                  templateType,
                                              )
                                    }
                                    disabled={
                                        isSubmitting ||
                                        !canEditMapping(templateType)
                                    }
                                >
                                    <i className="bi bi-bezier2" />
                                </button>
                            </div>
                        </div>

                        {templateType.importTypeId &&
                            !hasDefinedFields(templateType) && (
                                <div className={style.rowHint}>
                                    {getText('msg_903d5df3957e')}
                                </div>
                            )}
                    </div>
                ))}

                <div className={style.importTypeFooter}>
                    <button
                        type="button"
                        onClick={handleAddImportType}
                        className={`ing-text-buttons p-0 border-0 bg-transparent ${style.addImportTypeAction}`}
                        disabled={isSubmitting}
                    >
                        <i
                            className="bi bi-plus-circle-fill me-2"
                            style={{
                                transform: 'scale(1.4)',
                                color: '#14BA6D',
                            }}
                        />
                        {addButtonLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

DefineImportTypesTab.propTypes = {
    templateData: PropTypes.object.isRequired,
    setTemplateData: PropTypes.func.isRequired,
    onEditFields: PropTypes.func.isRequired,
    onEditMapping: PropTypes.func.isRequired,
    isSubmitting: PropTypes.bool,
};

export default DefineImportTypesTab;
